// src/auth.ts
import {
  User,
  UserManager,
  WebStorageStateStore,
  Log,
} from 'oidc-client-ts';

// Optional: verbose logs during dev
if (import.meta.env.DEV) {
  Log.setLogger(console);
  Log.setLevel(Log.DEBUG);
}

//
//
// const authority = import.meta.env.VITE_OIDC_AUTHORITY as string;
// const client_id = import.meta.env.VITE_OIDC_CLIENT_ID as string;
// const redirect_uri = import.meta.env.VITE_OIDC_REDIRECT_URI as string;
// const post_logout_redirect_uri = import.meta.env
//   .VITE_OIDC_POST_LOGOUT_REDIRECT_URI as string;
//
// if (!authority || !client_id || !redirect_uri || !post_logout_redirect_uri) {
//   console.error("Missing OIDC env vars. Check your .env* files.");
// }

// Use runtime config from window if available, fallback to env vars for local dev
declare global {
  interface Window {
    APP_CONFIG?: {
      API_BASE: string;
      OIDC_AUTHORITY: string;
      OIDC_CLIENT_ID: string;
      COGNITO_REGION: string;
      COGNITO_POOL_ID: string;
    };
  }
}

// Get base URL for the app (handles both dev and production)
const getAppBaseUrl = () => {
  if (import.meta.env.DEV) {
    // Local development
    return window.location.origin;
  } else {
    // Production - use the actual domain
    return window.location.origin + '/app';
  }
};

const base   = window.APP_CONFIG?.OIDC_AUTHORITY || import.meta.env.VITE_OIDC_AUTHORITY as string;
const cid    = window.APP_CONFIG?.OIDC_CLIENT_ID || import.meta.env.VITE_OIDC_CLIENT_ID as string;
const region = window.APP_CONFIG?.COGNITO_REGION || import.meta.env.VITE_COGNITO_REGION as string;
const poolId = window.APP_CONFIG?.COGNITO_POOL_ID || import.meta.env.VITE_COGNITO_POOL_ID as string;

// Dynamic redirect URIs based on current location
const ruri   = import.meta.env.DEV 
  ? (import.meta.env.VITE_OIDC_REDIRECT_URI as string)
  : `${getAppBaseUrl()}/callback`;
const louri  = import.meta.env.DEV
  ? (import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI as string)  
  : `${getAppBaseUrl()}/`;


export const mgr = new UserManager({
  authority: base,                     // keep for issuer match
  client_id: cid,
  redirect_uri: ruri,
  post_logout_redirect_uri: louri,
  response_type: 'code',
  scope: 'openid email profile',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  silent_redirect_uri: new URL('/silent-renew.html', window.location.origin).toString(),

  // 👇 This avoids the CORS-blocked discovery request
  metadata: {
    issuer: base,
    authorization_endpoint: `${base}/oauth2/authorize`,
    token_endpoint:         `${base}/oauth2/token`,
    userinfo_endpoint:      `${base}/oauth2/userInfo`,
    end_session_endpoint:   `${base}/logout`,
    revocation_endpoint:    `${base}/oauth2/revoke`,
    // JWKS comes from the *region+pool* endpoint, not your custom domain
    jwks_uri: `https://cognito-idp.${region}.amazonaws.com/${poolId}/.well-known/jwks.json`,
  },
});

// --- Convenience API ---

export async function login(): Promise<void> {
  await mgr.signinRedirect();
}

export async function logout() {
  const user = await mgr.getUser(); // grab id_token before we navigate away
  await mgr.signoutRedirect({
    id_token_hint: user?.id_token,  // harmless if not used by Cognito
    // Cognito requires these names specifically:
    extraQueryParams: {
      client_id: cid,
      logout_uri: louri,
    },
  });
}

export async function completeLogin(): Promise<User> {
  // Call this on /callback
  const user = await mgr.signinCallback();
  if (!user) {
    throw new Error('Failed to complete login: no user returned');
  }
  return user;
}

export async function getUser(): Promise<User | null> {
  return mgr.getUser();
}


export async function getIdToken(): Promise<string | null> {
  const u = await mgr.getUser();
  return u?.id_token ?? null;
}

export async function getAccessToken(): Promise<string | null> {
  const u = await mgr.getUser();
  return u?.access_token ?? null;
}

export function onAuthEvents() {
  mgr.events.addUserLoaded((_u) => {
    // Debug: User loaded
  });
  mgr.events.addUserUnloaded(() => {
    // Debug: User unloaded
  });
  mgr.events.addAccessTokenExpiring(() => {
    // Debug: Access token expiring
  });
  mgr.events.addAccessTokenExpired(async () => {
    // Debug: Access token expired
    // automaticSilentRenew should kick in; if not, you could trigger:
    // await mgr.signinSilent().catch(() => login());
  });
  mgr.events.addSilentRenewError((e) => {
    console.warn('[oidc] silent renew error', e);
  });
}

